/**
 * WDK Service for SDK
 *
 * Native integration with Tether WDK for multi-chain address derivation.
 * Uses individual wallet modules for each chain to derive cryptographically
 * valid addresses from a BIP-39 seed phrase.
 *
 * Supported chains: Ethereum, Bitcoin, TON, TRON, Solana, Spark (Lightning)
 *
 * @see https://docs.wallet.tether.io/
 */

// Dynamic imports for ESM modules (WDK packages are ESM-only)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let WdkManager: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let WalletManagerBtc: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let WalletManagerEvm: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let WalletManagerSolana: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let WalletManagerTon: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let WalletManagerTron: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let WalletManagerSpark: any;

let wdkLoaded = false;
let wdkLoadError: Error | null = null;

/**
 * Dynamic import helper that bypasses TypeScript's CommonJS transformation
 * TypeScript converts import() to require() in CommonJS, but WDK is ESM-only
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dynamicImport = new Function('specifier', 'return import(specifier)') as (
  specifier: string
) => Promise<any>;

/**
 * Dynamically load WDK modules (they are ESM-only)
 */
async function loadWdkModules(): Promise<void> {
  if (wdkLoaded) return;
  if (wdkLoadError) throw wdkLoadError;

  try {
    const [wdk, btc, evm, solana, ton, tron, spark] = await Promise.all([
      dynamicImport('@tetherto/wdk'),
      dynamicImport('@tetherto/wdk-wallet-btc'),
      dynamicImport('@tetherto/wdk-wallet-evm'),
      dynamicImport('@tetherto/wdk-wallet-solana'),
      dynamicImport('@tetherto/wdk-wallet-ton'),
      dynamicImport('@tetherto/wdk-wallet-tron'),
      dynamicImport('@tetherto/wdk-wallet-spark'),
    ]);

    WdkManager = wdk.default;
    WalletManagerBtc = btc.default;
    WalletManagerEvm = evm.default;
    WalletManagerSolana = solana.default;
    WalletManagerTon = ton.default;
    WalletManagerTron = tron.default;
    WalletManagerSpark = spark.default;
    wdkLoaded = true;
  } catch (error) {
    wdkLoadError = error instanceof Error ? error : new Error('Failed to load WDK modules');
    console.error('Failed to load WDK modules:', error);
    throw wdkLoadError;
  }
}

export type SupportedChain = 'ethereum' | 'bitcoin' | 'ton' | 'tron' | 'solana' | 'spark';

export interface ChainAddress {
  chain: SupportedChain;
  address: string;
  path: string;
}

export interface MultiChainAddresses {
  ethereum: string | null;
  bitcoin: string | null;
  ton: string | null;
  tron: string | null;
  solana: string | null;
  spark: string | null;
}

export interface WdkServiceConfig {
  network: 'mainnet' | 'testnet';
  rpcUrls?: {
    ethereum?: string;
    bitcoin?: string;
    solana?: string;
    ton?: string;
    tron?: string;
  };
}

// BIP-44 derivation paths
const DERIVATION_PATHS: Record<SupportedChain, string> = {
  bitcoin: "m/84'/0'/0'/0/0", // BIP-84 for native SegWit
  ethereum: "m/44'/60'/0'/0/0",
  ton: "m/44'/607'/0'", // Updated for v1.0.0-beta.6+
  tron: "m/44'/195'/0'/0/0",
  solana: "m/44'/501'/0'/0'", // Updated for v1.0.0-beta.4+
  spark: "m/44'/998'/0'/0/0",
};

// Default RPC URLs
const DEFAULT_RPC_URLS = {
  mainnet: {
    ethereum: 'https://eth.llamarpc.com',
    solana: 'https://api.mainnet-beta.solana.com',
    ton: 'https://toncenter.com/api/v2/jsonRPC',
    tron: 'https://api.trongrid.io',
  },
  testnet: {
    ethereum: 'https://ethereum-sepolia-rpc.publicnode.com',
    solana: 'https://api.devnet.solana.com',
    ton: 'https://testnet.toncenter.com/api/v2/jsonRPC',
    tron: 'https://api.shasta.trongrid.io',
  },
};

/**
 * WDK Service for native multi-chain address derivation
 * Uses Tether WDK wallet modules directly without needing a backend API
 */
export class WdkService {
  private seed: string | null = null;
  private config: WdkServiceConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private wallets: Partial<Record<SupportedChain, any>> = {};

  constructor(config: Partial<WdkServiceConfig> = {}) {
    this.config = {
      network: config.network || 'testnet',
      rpcUrls: config.rpcUrls,
    };
  }

  /**
   * Check if WDK modules are loaded
   */
  static isLoaded(): boolean {
    return wdkLoaded;
  }

  /**
   * Ensure WDK modules are loaded
   */
  private async ensureLoaded(): Promise<void> {
    await loadWdkModules();
  }

  /**
   * Load WDK modules (call this before using sync methods)
   */
  async loadModules(): Promise<void> {
    await loadWdkModules();
  }

  /**
   * Generate a random BIP-39 seed phrase (12 words)
   */
  async generateSeedPhrase(): Promise<string> {
    await this.ensureLoaded();
    return WdkManager.getRandomSeedPhrase();
  }

  /**
   * Validate a BIP-39 seed phrase
   */
  async isValidSeed(seed: string): Promise<boolean> {
    await this.ensureLoaded();
    return WdkManager.isValidSeed(seed);
  }

  /**
   * Validate seed phrase (sync version - basic check)
   */
  isValidSeedSync(seed: string): boolean {
    const words = seed.trim().split(/\s+/);
    return words.length === 12 || words.length === 24;
  }

  /**
   * Initialize the service with a seed phrase
   */
  async initialize(seed: string): Promise<void> {
    await this.ensureLoaded();

    if (!WdkManager.isValidSeed(seed)) {
      throw new Error('Invalid seed phrase');
    }

    this.seed = seed;
    this.wallets = {};
  }

  /**
   * Get RPC URL for a chain
   */
  private getRpcUrl(chain: SupportedChain): string {
    const networkUrls = DEFAULT_RPC_URLS[this.config.network];

    if (this.config.rpcUrls?.[chain as keyof typeof this.config.rpcUrls]) {
      return this.config.rpcUrls[chain as keyof typeof this.config.rpcUrls]!;
    }

    return networkUrls[chain as keyof typeof networkUrls] || '';
  }

  /**
   * Get or create wallet instance for a specific chain
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async getWallet(chain: SupportedChain): Promise<any> {
    if (!this.seed) {
      throw new Error('WDK service not initialized. Call initialize() first.');
    }

    // Return cached wallet if exists
    if (this.wallets[chain]) {
      return this.wallets[chain];
    }

    const isTestnet = this.config.network === 'testnet';

    try {
      switch (chain) {
        case 'ethereum': {
          const rpcUrl = this.getRpcUrl('ethereum');
          const wallet = new WalletManagerEvm(this.seed, { provider: rpcUrl });
          this.wallets[chain] = wallet;
          return wallet;
        }

        case 'bitcoin': {
          const wallet = new WalletManagerBtc(this.seed, {
            network: isTestnet ? 'testnet' : 'bitcoin',
          });
          this.wallets[chain] = wallet;
          return wallet;
        }

        case 'solana': {
          const rpcUrl = this.getRpcUrl('solana');
          const wallet = new WalletManagerSolana(this.seed, {
            rpcUrl,
          });
          this.wallets[chain] = wallet;
          return wallet;
        }

        case 'ton': {
          const url = this.getRpcUrl('ton');
          const wallet = new WalletManagerTon(this.seed, {
            tonClient: { url },
          });
          this.wallets[chain] = wallet;
          return wallet;
        }

        case 'tron': {
          const fullHost = this.getRpcUrl('tron');
          const wallet = new WalletManagerTron(this.seed, {
            provider: fullHost,
          });
          this.wallets[chain] = wallet;
          return wallet;
        }

        case 'spark': {
          const wallet = new WalletManagerSpark(this.seed, {
            network: isTestnet ? 'TESTNET' : 'MAINNET',
          });
          this.wallets[chain] = wallet;
          return wallet;
        }

        default:
          throw new Error(`Unsupported chain: ${chain}`);
      }
    } catch (error) {
      console.error(`Failed to initialize ${chain} wallet:`, error);
      throw error;
    }
  }

  /**
   * Derive address for a specific chain
   */
  async deriveAddress(chain: SupportedChain): Promise<ChainAddress> {
    const path = DERIVATION_PATHS[chain];

    try {
      const wallet = await this.getWallet(chain);
      const account = await wallet.getAccount(0);
      const address = await account.getAddress();

      return {
        chain,
        address,
        path,
      };
    } catch (error) {
      console.error(`Error deriving ${chain} address:`, error);
      throw error;
    }
  }

  /**
   * Derive addresses for all supported chains
   */
  async deriveAllAddresses(): Promise<MultiChainAddresses> {
    const chains: SupportedChain[] = ['ethereum', 'bitcoin', 'ton', 'tron', 'solana', 'spark'];
    const addresses: MultiChainAddresses = {
      ethereum: null,
      bitcoin: null,
      ton: null,
      tron: null,
      solana: null,
      spark: null,
    };

    // Derive all addresses in parallel for better performance
    const results = await Promise.allSettled(
      chains.map(async (chain) => {
        const result = await this.deriveAddress(chain);
        return { chain, address: result.address };
      })
    );

    for (const result of results) {
      if (result.status === 'fulfilled') {
        addresses[result.value.chain] = result.value.address;
      } else {
        console.error('Failed to derive address:', result.reason);
      }
    }

    return addresses;
  }

  /**
   * Derive addresses for specific chains only
   */
  async deriveAddressesForChains(chains: SupportedChain[]): Promise<Partial<MultiChainAddresses>> {
    const addresses: Partial<MultiChainAddresses> = {};

    const results = await Promise.allSettled(
      chains.map(async (chain) => {
        const result = await this.deriveAddress(chain);
        return { chain, address: result.address };
      })
    );

    for (const result of results) {
      if (result.status === 'fulfilled') {
        addresses[result.value.chain] = result.value.address;
      }
    }

    return addresses;
  }

  /**
   * Get fee rates for a specific chain
   */
  async getFeeRates(
    chain: SupportedChain
  ): Promise<{ slow: string; medium: string; fast: string }> {
    if (!this.seed) {
      throw new Error('WDK service not initialized. Call initialize() first.');
    }

    try {
      const wallet = await this.getWallet(chain);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const feeRates: any = await wallet.getFeeRates();
      return {
        slow: (feeRates.slow || feeRates.low || '0').toString(),
        medium: (feeRates.medium || feeRates.normal || feeRates.standard || '0').toString(),
        fast: (feeRates.fast || feeRates.high || '0').toString(),
      };
    } catch (error) {
      console.error(`Error fetching fee rates for ${chain}:`, error);
      throw error;
    }
  }

  /**
   * Get the current network configuration
   */
  getNetwork(): 'mainnet' | 'testnet' {
    return this.config.network;
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.seed !== null;
  }

  /**
   * Clean up and dispose of wallet instances
   */
  dispose(): void {
    for (const wallet of Object.values(this.wallets)) {
      if (wallet && typeof wallet.dispose === 'function') {
        try {
          wallet.dispose();
        } catch {
          // Ignore dispose errors
        }
      }
    }
    this.wallets = {};
    this.seed = null;
  }
}

// Singleton instance
let wdkServiceInstance: WdkService | null = null;

/**
 * Get or create the WDK service singleton
 */
export function getWdkService(config?: Partial<WdkServiceConfig>): WdkService {
  if (!wdkServiceInstance || (config && config.network !== wdkServiceInstance.getNetwork())) {
    wdkServiceInstance = new WdkService(config);
  }
  return wdkServiceInstance;
}

/**
 * Create a new WDK service instance (non-singleton)
 */
export function createWdkService(config?: Partial<WdkServiceConfig>): WdkService {
  return new WdkService(config);
}
